// Manual mode: keeps the vessel's draft as an uncomputed sketch in step with the endpoint fields (see README).

import { useCallback, useEffect, useRef } from "react"
import { normalizeSketchRoute } from "@/features/route/state/RouteAdapters.jsx"
import { selectRenderableRoute } from "@/features/route/state/RouteSelectors.jsx"
import { createDraftDiscardState } from "@/features/route/state/RouteState.jsx"
import {
  buildManualFeatures,
  isManualDraft,
  legtypeOf,
  manualEndpointsMatch,
  manualMiddlePoints,
  manualStartLegtype,
} from "@/features/voyagePlanner/manualSketch"

const EMPTY = []

const isLonLat = (coords) =>
  Array.isArray(coords) &&
  coords.length === 2 &&
  coords.every((value) => Number.isFinite(Number(value)))

export default function useManualSketch({
  enabled,
  startEndpoint,
  finishEndpoint,
  vesselId,
  draftRouteId,
  draftRenderable,
  routeStoreState,
  replaceRoute,
  buildDraftTarget,
  vesselLabel,
}) {
  const manualDraft = isManualDraft(draftRenderable) ? draftRenderable : null

  // Stable identity while the geometry is unchanged (feeds the planner-points effect).
  const rawMiddles = manualDraft ? manualMiddlePoints(manualDraft) : EMPTY
  const middlesKey = JSON.stringify(
    rawMiddles.map((f) => [f.geometry?.coordinates, legtypeOf(f)]),
  )
  const middlesRef = useRef({ key: null, value: EMPTY })
  if (middlesRef.current.key !== middlesKey) {
    middlesRef.current = { key: middlesKey, value: rawMiddles }
  }
  const manualMiddles = middlesRef.current.value
  const startLegtype = manualStartLegtype(manualDraft)

  const latestRef = useRef({})
  latestRef.current = {
    manualDraft,
    // A null id still selects an empty renderable (route 0).
    draftRenderable: draftRouteId == null ? null : draftRenderable,
    draftRouteId,
    routeStoreState,
    buildDraftTarget,
    vesselLabel,
  }

  // A sketch drawn before any vessel was chosen moves to the first vessel picked.
  const vesselless = !Number.isFinite(Number(vesselId))
  const orphanRef = useRef(null)
  if (vesselless) orphanRef.current = draftRenderable?.sketch ? draftRouteId : null

  const writeSketch = useCallback(
    (features, routeIdOverride = null) => {
      const { buildDraftTarget: target, vesselLabel: label } = latestRef.current
      const { draftRouteId: slotId, draftContext } = target()
      const routeId = routeIdOverride ?? slotId
      replaceRoute(
        routeId,
        normalizeSketchRoute(routeId, features, {
          draftContext,
          vesselName: label,
        }),
      )
    },
    [replaceRoute],
  )

  const discardSketch = useCallback(() => {
    const { draftRenderable: slot, draftRouteId: routeId } = latestRef.current
    if (routeId == null || !slot?.sketch) return
    replaceRoute(routeId, createDraftDiscardState(routeId))
  }, [replaceRoute])

  // Endpoint fields → sketch: rewrites a manual draft whose ends moved, fills an empty slot, never touches time/fuel.
  useEffect(() => {
    if (!enabled) return
    const start = startEndpoint?.coordinates
    const finish = finishEndpoint?.coordinates
    if (!isLonLat(start) || !isLonLat(finish)) return
    const {
      manualDraft: draft,
      draftRenderable: slot,
      routeStoreState: state,
    } = latestRef.current
    // The picked vessel brings its own draft: the vessel-less sketch is obsolete.
    if (slot && !vesselless && orphanRef.current != null) {
      replaceRoute(orphanRef.current, createDraftDiscardState(orphanRef.current))
      orphanRef.current = null
    }
    if (slot && !draft) return
    if (draft && manualEndpointsMatch(draft, start, finish)) return
    const orphan =
      !slot && !vesselless && orphanRef.current != null
        ? selectRenderableRoute(state, orphanRef.current)
        : null
    const source = draft ?? (orphan?.sketch ? orphan : null)
    orphanRef.current = null
    writeSketch(
      buildManualFeatures({
        start,
        middles: source ? manualMiddlePoints(source) : [],
        finish,
        startLegtype: manualStartLegtype(source),
      }),
      draft ? null : (source?.points?.routeId ?? null),
    )
  }, [
    enabled,
    startEndpoint,
    finishEndpoint,
    draftRouteId,
    vesselless,
    replaceRoute,
    writeSketch,
  ])

  const showSketch =
    enabled &&
    isLonLat(startEndpoint?.coordinates) &&
    isLonLat(finishEndpoint?.coordinates)

  return {
    manualMiddles,
    startLegtype,
    isSketch: draftRenderable?.sketch === true,
    showSketch,
    discardSketch,
  }
}
